import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Tag,
  Tabs,
  Checkbox,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GiftOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthService from '../utils/auth';

const { TextArea } = Input;
const { Option } = Select;

const AddOnProductManagement = () => {
  const navigate = useNavigate();
  const [addOnProducts, setAddOnProducts] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [allAttachments, setAllAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterDestination, setFilterDestination] = useState(null);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [form] = Form.useForm();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchDestinations();
    fetchAttachments();
  }, []);

  useEffect(() => {
    fetchAddOnProducts();
  }, [filterDestination]);

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/destinations?isActive=true`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setDestinations(response.data.data);
      }
    } catch (error) {
      console.error('获取目的地列表失败:', error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await axios.get(`${API_BASE}/notice-attachments?isActive=true`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setAllAttachments(response.data.data);
      }
    } catch (error) {
      console.error('获取附件列表失败:', error);
    }
  };

  const fetchAddOnProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDestination) params.append('destinationId', filterDestination);

      const response = await axios.get(`${API_BASE}/addon-products?${params.toString()}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setAddOnProducts(response.data.data);
      }
    } catch (error) {
      message.error('获取Add-ons产品列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setSelectedAttachmentIds([]);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    setSelectedAttachmentIds(record.attachmentIds || []);
    form.setFieldsValue({
      destinationId: record.destinationId,
      name: record.name,
      dayNumber: record.dayNumber,
      meetingTime: record.meetingTime,
      meetingPoint: record.meetingPoint,
      summary: record.summary,
      description: record.description,
      duration: record.duration,
      includedItems: record.includedItems,
      excludedItems: record.excludedItems,
      notes: record.notes,
      sortOrder: record.sortOrder,
      isActive: record.isActive
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/addon-products/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        message.success('删除成功');
        fetchAddOnProducts();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        attachmentIds: selectedAttachmentIds
      };

      if (editingProduct) {
        await axios.put(`${API_BASE}/addon-products/${editingProduct.id}`, data, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('更新成功');
      } else {
        await axios.post(`${API_BASE}/addon-products`, data, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchAddOnProducts();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    }
  };

  const columns = [
    {
      title: '目的地',
      dataIndex: ['destination', 'name'],
      key: 'destination',
      width: 90,
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <GiftOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      )
    },
    {
      title: '行程天数',
      dataIndex: 'dayNumber',
      key: 'dayNumber',
      width: 90,
      render: (day) => <Tag color="blue">第{day}天</Tag>
    },
    {
      title: '集合时间',
      dataIndex: 'meetingTime',
      key: 'meetingTime',
      width: 90
    },
    {
      title: '集合地点',
      dataIndex: 'meetingPoint',
      key: 'meetingPoint',
      ellipsis: true
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 90
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="通知书模板">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => navigate(`/admin/addon-products/${record.id}/notice-template`)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除该Add-ons产品吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="Add-ons产品管理"
        extra={
          <Space>
            <Select
              placeholder="筛选目的地"
              style={{ width: 150 }}
              allowClear
              value={filterDestination}
              onChange={setFilterDestination}
            >
              {destinations.map(d => (
                <Option key={d.id} value={d.id}>{d.name}</Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新建Add-ons产品
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={addOnProducts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
          size="small"
        />
      </Card>

      <Modal
        title={editingProduct ? '编辑Add-ons产品' : '新建Add-ons产品'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Tabs
            items={[
              {
                key: 'basic',
                label: '基本信息',
                children: (
                  <>
                    <Form.Item
                      name="destinationId"
                      label="所属目的地"
                      rules={[{ required: true, message: '请选择目的地' }]}
                    >
                      <Select placeholder="选择目的地">
                        {destinations.map(d => (
                          <Option key={d.id} value={d.id}>{d.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="name"
                      label="产品名称"
                      rules={[{ required: true, message: '请输入产品名称' }]}
                    >
                      <Input placeholder="如：伊瓜苏瀑布一日游" />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name="dayNumber"
                          label="行程第几天"
                          rules={[{ required: true, message: '请输入天数' }]}
                        >
                          <InputNumber min={1} max={30} style={{ width: '100%' }} placeholder="如：3" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="meetingTime" label="集合时间">
                          <Input placeholder="如：08:00" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="duration" label="活动时长">
                          <Input placeholder="如：约8小时" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item name="meetingPoint" label="集合地点">
                      <Input placeholder="如：酒店大堂" />
                    </Form.Item>

                    <Form.Item name="summary" label="摘要">
                      <Input placeholder="简短描述（显示在通知书列表中）" />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="sortOrder" label="排序">
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="isActive" label="状态" valuePropName="checked">
                          <Switch checkedChildren="启用" unCheckedChildren="停用" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )
              },
              {
                key: 'detail',
                label: '详细说明',
                children: (
                  <Form.Item name="description" label="详细说明">
                    <TextArea rows={10} placeholder="活动的详细介绍..." />
                  </Form.Item>
                )
              },
              {
                key: 'include',
                label: '包含/不包含',
                children: (
                  <>
                    <Form.Item name="includedItems" label="包含项目">
                      <TextArea rows={5} placeholder="如：往返交通、门票、中文导游、午餐..." />
                    </Form.Item>
                    <Form.Item name="excludedItems" label="不包含项目">
                      <TextArea rows={5} placeholder="如：小费、个人消费..." />
                    </Form.Item>
                  </>
                )
              },
              {
                key: 'notes',
                label: '注意事项',
                children: (
                  <Form.Item name="notes" label="注意事项">
                    <TextArea rows={8} placeholder="参加该活动的注意事项..." />
                  </Form.Item>
                )
              },
              {
                key: 'attachments',
                label: '附件',
                children: (
                  <div>
                    <p style={{ color: '#666', marginBottom: 16 }}>
                      选择需要包含的附件
                    </p>
                    <Checkbox.Group
                      value={selectedAttachmentIds}
                      onChange={setSelectedAttachmentIds}
                      style={{ width: '100%' }}
                    >
                      <Row gutter={[16, 16]}>
                        {allAttachments.map((att) => (
                          <Col key={att.id} span={12}>
                            <Card size="small" hoverable>
                              <Checkbox value={att.id}>
                                <Space direction="vertical" size={0}>
                                  <span>{att.name}</span>
                                  {att.category && (
                                    <Tag color="blue" style={{ marginTop: 4 }}>
                                      {att.category}
                                    </Tag>
                                  )}
                                </Space>
                              </Checkbox>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </div>
                )
              }
            ]}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default AddOnProductManagement;
